import { TFunction } from "i18next";

import { ColumnDefinition } from "../EmployeeDataDrawer";

export interface SalaryGroup {
  id: string;
  labelKey: string;
  defaultLabel: string;
}

export const SALARY_GROUPS: SalaryGroup[] = [
  { id: "basePay", labelKey: "paygap.employeeData.group.basePay", defaultLabel: "Base Pay" },
  { id: "allowances", labelKey: "paygap.employeeData.group.allowances", defaultLabel: "Allowances" },
  { id: "personalBonuses", labelKey: "paygap.employeeData.group.personalBonuses", defaultLabel: "Personal Bonuses" },
  { id: "specialBonuses", labelKey: "paygap.employeeData.group.specialBonuses", defaultLabel: "Special Bonuses and Benefits" },
];

/**
 * Get column definitions with translations.
 * @param t - Translation function from useTranslation hook
 */
export const getColumnDefinitions = (t: TFunction): ColumnDefinition[] => [
  { key: "key", label: t("paygap.employeeData.column.key") },
  { key: "age", label: t("paygap.employeeData.column.age") },
  { key: "gender", label: t("paygap.employeeData.column.gender") },
  { key: "yearsOfService", label: t("paygap.employeeData.column.yearsOfService") },
  {
    key: "educationAndTraining",
    label: t("paygap.employeeData.column.educationAndTraining"),
  },
  {
    key: "employeeScarcity",
    label: t("paygap.employeeData.column.employeeScarcity", "Employee Scarcity"),
  },
  {
    key: "employeeEvaluation",
    label: t("paygap.employeeData.column.employeeEvaluation", "Employee Evaluation"),
  },
  { key: "category", label: t("paygap.employeeData.column.category") },
  { key: "function", label: t("paygap.employeeData.column.function") },
  {
    key: "levelOfRequirements",
    label: t("paygap.table.levelOfRequirements1"),
  },
  {
    key: "levelOfRequirements2",
    label: t("paygap.table.levelOfRequirements2"),
  },
  {
    key: "levelOfRequirements3",
    label: t("paygap.table.levelOfRequirements3"),
  },
  {
    key: "professionalPosition",
    label: t("paygap.employeeData.column.professionalPosition"),
  },
  {
    key: "activityRatePercent",
    label: t("paygap.employeeData.column.activityRatePercent"),
  },
  { key: "paidHours", label: t("paygap.employeeData.column.paidHours") },

  // GROUP 1: Base Pay
  {
    key: "basicWage",
    label: t("paygap.employeeData.column.basicWage"),
    group: "basePay",
    isGroupGeneral: true,
  },
  {
    key: "wageCompensationForVacation",
    label: t(
      "paygap.employeeData.column.wageCompensationForVacation",
      "Wage Compensation for Vacation",
    ),
    group: "basePay",
  },
  {
    key: "wageCompensationDoctorFamilyLeave",
    label: t(
      "paygap.employeeData.column.wageCompensationDoctorFamilyLeave",
      "Wage Compensation for Doctor and Family Leave",
    ),
    group: "basePay",
  },
  {
    key: "sickLeaveIncomeCompensation",
    label: t(
      "paygap.employeeData.column.sickLeaveIncomeCompensation",
      "Sick Leave Income Compensation",
    ),
    group: "basePay",
  },

  // GROUP 2: Allowances
  {
    key: "allowances",
    label: t("paygap.employeeData.column.allowances"),
    group: "allowances",
    isGroupGeneral: true,
  },
  {
    key: "overtimeWage",
    label: t("paygap.employeeData.column.overtimeWage", "Overtime Wage"),
    group: "allowances",
  },
  {
    key: "overtimeAllowance",
    label: t("paygap.employeeData.column.overtimeAllowance", "Overtime Allowance"),
    group: "allowances",
  },
  {
    key: "riskAllowance",
    label: t("paygap.employeeData.column.riskAllowance", "Risk Allowance"),
    group: "allowances",
  },
  {
    key: "standbyDuty",
    label: t("paygap.employeeData.column.standbyDuty", "Standby Duty"),
    group: "allowances",
  },

  // GROUP 3: Personal Bonuses
  {
    key: "thirteenthMonthlyWage",
    label: t("paygap.employeeData.column.thirteenthMonthlyWage"),
    group: "personalBonuses",
    isGroupGeneral: true,
  },
  {
    key: "monthlyBonus",
    label: t("paygap.employeeData.column.monthlyBonus", "Monthly Bonus"),
    group: "personalBonuses",
  },
  {
    key: "standardBonus",
    label: t("paygap.employeeData.column.standardBonus"),
    group: "personalBonuses",
  },
  {
    key: "specialBonus",
    label: t("paygap.employeeData.column.specialBonus"),
    group: "personalBonuses",
  },
  {
    key: "fiftyFivePlusBonus",
    label: t("paygap.employeeData.column.fiftyFivePlusBonus"),
    group: "personalBonuses",
  },
  {
    key: "retirementBenefit",
    label: t("paygap.employeeData.column.retirementBenefit", "Retirement Benefit"),
    group: "personalBonuses",
  },
  {
    key: "severancePay",
    label: t("paygap.employeeData.column.severancePay"),
    group: "personalBonuses",
  },

  // GROUP 4: Special Bonuses and Benefits
  {
    key: "specialPayments",
    label: t("paygap.employeeData.column.specialPayments"),
    group: "specialBonuses",
    isGroupGeneral: true,
  },
  {
    key: "supplementaryPensionInsurance",
    label: t("paygap.employeeData.column.supplementaryPensionInsurance"),
    group: "specialBonuses",
  },
  {
    key: "lifeInsurance",
    label: t("paygap.employeeData.column.lifeInsurance"),
    group: "specialBonuses",
  },
  {
    key: "mealVouchers",
    label: t("paygap.employeeData.column.mealVouchers"),
    group: "specialBonuses",
  },
  {
    key: "cafeteria",
    label: t("paygap.employeeData.column.cafeteria"),
    group: "specialBonuses",
  },
  {
    key: "carAllowance",
    label: t("paygap.employeeData.column.carAllowance"),
    group: "specialBonuses",
  },

  // Trailing columns
  {
    key: "employeeContribution",
    label: t("paygap.employeeData.column.employeeContribution", "Employee Contribution"),
  },
  {
    key: "usualWeeklyWorkingHours",
    label: t("paygap.employeeData.column.usualWeeklyWorkingHours"),
  },
  {
    key: "usualAnnualWorkingHours",
    label: t("paygap.employeeData.column.usualAnnualWorkingHours"),
  },
  {
    key: "statisticalPopulation",
    label: t("paygap.employeeData.column.statisticalPopulation"),
  },
  {
    key: "remarks",
    label: t("paygap.employeeData.column.remarks", "Remarks"),
  },
  {
    key: "additionalInformation01",
    label: t("paygap.employeeData.column.additionalInformation01", "Additional Information 1"),
  },
  {
    key: "additionalInformation02",
    label: t("paygap.employeeData.column.additionalInformation02", "Additional Information 2"),
  },
  {
    key: "additionalInformation03",
    label: t("paygap.employeeData.column.additionalInformation03", "Additional Information 3"),
  },
  {
    key: "additionalInformation04",
    label: t("paygap.employeeData.column.additionalInformation04", "Additional Information 4"),
  },
  {
    key: "additionalInformation05",
    label: t("paygap.employeeData.column.additionalInformation05", "Additional Information 5"),
  },
];

/**
 * Get column label by key
 */
export const getColumnLabel = (
  columns: ColumnDefinition[],
  columnKey: string,
): string => {
  const column = columns.find((col) => col.key === columnKey);

  return column?.label || columnKey;
};
